import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useAppInstallPrompt } from '../hooks/useAppInstallPrompt';
import { useFinePointerDevice } from '../hooks/useFinePointerDevice';
import { isAssetOwnerLoginType } from '../auth/userLoginType';
import { useI18n } from '../i18n';
import type { AppView } from '../navigation/AppNavigationContext';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { useMessagingInbox } from '../messaging/MessagingInboxContext';
import styles from './BottomNav.module.css';

type BottomNavProps = {
  activeView: AppView;
};

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5z" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  );
}

function AssetsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M4 20V8l8-4 8 4v12" />
      <path d="M9 20v-6h6v6" />
      <path d="M4 8l8 4 8-4" />
    </svg>
  );
}

function FavoritesIcon({ filled = false }: { filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.75"
    >
      <path d="M12 20.5 4.5 12.8a5.2 5.2 0 0 1 0-7.4 5.2 5.2 0 0 1 7.4 0L12 5.5l.1-.1a5.2 5.2 0 0 1 7.4 0 5.2 5.2 0 0 1 0 7.4L12 20.5z" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <circle cx="12" cy="8" r="4" />
      <path d="M5 20c0-4 3.5-7 7-7s7 3 7 7" />
    </svg>
  );
}

function MessengerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden>
      <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.908 1.434 5.502 3.676 7.183V22l3.358-1.843c.896.248 1.844.382 2.966.382 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.053 12.446-2.568-2.742-5.019 2.742 5.524-5.873 2.637 2.742 4.947-2.742-5.521 5.873z" />
    </svg>
  );
}

function ContactIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M21 15.5v3a2 2 0 0 1-2.2 2 18 18 0 0 1-7.8-2.8 17.6 17.6 0 0 1-5.4-5.4A18 18 0 0 1 2.8 4.5 2 2 0 0 1 4.8 2.3h3a2 2 0 0 1 2 1.7c.1.9.3 1.7.6 2.5a2 2 0 0 1-.5 2.1l-1 1a14.5 14.5 0 0 0 5 5l1-1a2 2 0 0 1 2.1-.5c.8.3 1.6.5 2.5.6a2 2 0 0 1 1.7 2z" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="M12 3v11" />
      <path d="M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
      <path d="M16.4 12.7c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.7-.8-2.9-.8-1.5 0-2.9.9-3.7 2.2-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.2 1.2 0 1.6-.7 3-.7 1.4 0 1.8.7 3 .7 1.2 0 2-1.1 2.8-2.2.6-.9.9-1.7 1.1-2.2-2.6-1-2.7-3.6-2.7-3.9zM14.2 5.5c.6-.8 1.1-1.9 1-3-1 0-2.2.7-2.9 1.5-.6.7-1.1 1.8-1 2.9 1.1.1 2.2-.6 2.9-1.4z" />
    </svg>
  );
}

function AndroidIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
      <path d="M6 9h12v8.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 17.5V9zm2.4-4.7-.9-1.5a.4.4 0 0 1 .7-.4l.9 1.6c.6-.2 1.2-.3 1.9-.3s1.3.1 1.9.3l.9-1.6a.4.4 0 0 1 .7.4l-.9 1.5A5.3 5.3 0 0 1 17.3 8H6.7c.3-1.5 1-2.8 1.7-3.7zM4 10.5a1.2 1.2 0 0 1 2.4 0v4.2a1.2 1.2 0 0 1-2.4 0v-4.2zm13.6 0a1.2 1.2 0 0 1 2.4 0v4.2a1.2 1.2 0 0 1-2.4 0v-4.2zM9.3 19.6h1.9v2.2a1 1 0 0 1-1.9 0v-2.2zm3.5 0h1.9v2.2a1 1 0 0 1-1.9 0v-2.2z" />
    </svg>
  );
}

export function BottomNav({ activeView }: BottomNavProps) {
  const { t } = useI18n();
  const { isLoggedIn, userLoginType } = useAuth();
  const { navigateFromBottomBar, openProfileHubRow } = useAppNavigation();
  const { unreadTotal } = useMessagingInbox();
  const ownerAssetsTab = isAssetOwnerLoginType(userLoginType);
  const showDesktopNavExtras = useFinePointerDevice();
  const {
    platform: installPlatform,
    installed: appInstalled,
    canPromptInstall,
    promptInstall,
  } = useAppInstallPrompt();
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!downloadOpen) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const wrap = downloadWrapRef.current;
      if (wrap && event.target instanceof Node && !wrap.contains(event.target)) {
        setDownloadOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [downloadOpen]);

  const unreadLabel =
    unreadTotal > 0
      ? t('nav.messagesUnread', {
          count: unreadTotal > 99 ? '99+' : String(unreadTotal),
        })
      : t('nav.messages');

  const onHomePress = () => {
    if (!isLoggedIn) {
      if (!navigateFromBottomBar('auth')) {
        return;
      }
      return;
    }
    navigateFromBottomBar('home');
  };

  return (
    <nav className={styles.nav} aria-label={t('nav.main')}>
      <button
        type="button"
        className={`${styles.item}${activeView === 'home' ? ` ${styles.itemActive}` : ''}`}
        onClick={onHomePress}
      >
        <HomeIcon />
        <span className={styles.label}>{t('nav.home')}</span>
      </button>
      <button
        type="button"
        className={`${styles.item}${activeView === 'bookings' ? ` ${styles.itemActive}` : ''}`}
        onClick={() => navigateFromBottomBar('bookings')}
      >
        <CalendarIcon />
        <span className={styles.label}>{t('nav.bookings')}</span>
      </button>
      {showDesktopNavExtras ? (
        <button
          type="button"
          className={styles.item}
          title={t('nav.contact')}
          onClick={() => openProfileHubRow('contact')}
        >
          <ContactIcon />
          <span className={styles.label}>{t('nav.contact')}</span>
        </button>
      ) : null}
      <button
        type="button"
        className={`${styles.publish}${activeView === 'messages' ? ` ${styles.publishActive}` : ''}`}
        title={unreadLabel}
        aria-label={unreadLabel}
        aria-current={activeView === 'messages' ? 'page' : undefined}
        onClick={() => {
          navigateFromBottomBar('messages');
        }}
      >
        <span className={styles.publishIcon}>
          <MessengerIcon />
          {unreadTotal > 0 ? (
            <span className={styles.unreadDot} aria-hidden>
              {unreadTotal > 99 ? '99+' : unreadTotal}
            </span>
          ) : null}
        </span>
      </button>
      {ownerAssetsTab ? (
        <button
          type="button"
          className={`${styles.item}${activeView === 'assets' ? ` ${styles.itemActive}` : ''}`}
          onClick={() => navigateFromBottomBar('assets')}
        >
          <AssetsIcon />
          <span className={styles.label}>{t('nav.assets')}</span>
        </button>
      ) : (
        <button
          type="button"
          className={`${styles.item}${activeView === 'favorites' ? ` ${styles.itemActive} ${styles.itemFavoritesActive}` : ''}`}
          onClick={() => navigateFromBottomBar('favorites')}
        >
          <FavoritesIcon filled={activeView === 'favorites'} />
          <span className={styles.label}>{t('nav.favorites')}</span>
        </button>
      )}
      <button
        type="button"
        className={`${styles.item}${activeView === 'profile' ? ` ${styles.itemActive}` : ''}`}
        onClick={() => navigateFromBottomBar('profile')}
      >
        <ProfileIcon />
        <span className={styles.label}>{t('nav.profile')}</span>
      </button>
      {showDesktopNavExtras ? (
        <div className={styles.downloadWrap} ref={downloadWrapRef}>
          {downloadOpen ? (
            <div className={styles.downloadSheet} role="dialog" aria-label={t('nav.download')}>
              <p className={styles.downloadTitle}>
                {installPlatform === 'ios' ? <AppleIcon /> : null}
                {installPlatform === 'android' ? <AndroidIcon /> : null}
                <span>{t('nav.installTitle')}</span>
              </p>
              {appInstalled ? (
                <p className={styles.downloadHint}>{t('nav.installDone')}</p>
              ) : (
                <>
                  {canPromptInstall ? (
                    <button
                      type="button"
                      className={styles.downloadAction}
                      onClick={() => {
                        setDownloadOpen(false);
                        void promptInstall();
                      }}
                    >
                      {t('nav.installNow')}
                    </button>
                  ) : null}
                  {installPlatform === 'ios' ? (
                    <ol className={styles.downloadSteps}>
                      <li>{t('nav.installIosStep1')}</li>
                      <li>{t('nav.installIosStep2')}</li>
                      <li>{t('nav.installIosStep3')}</li>
                    </ol>
                  ) : null}
                  {installPlatform === 'android' ? (
                    <ol className={styles.downloadSteps}>
                      <li>{t('nav.installAndroidStep1')}</li>
                      <li>{t('nav.installAndroidStep2')}</li>
                    </ol>
                  ) : null}
                  {installPlatform === 'desktop' ? (
                    <p className={styles.downloadHint}>{t('nav.installDesktopHint')}</p>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
          <button
            type="button"
            className={`${styles.item}${downloadOpen ? ` ${styles.itemActive}` : ''}`}
            aria-haspopup="menu"
            aria-expanded={downloadOpen}
            aria-label={downloadOpen ? t('nav.downloadClose') : t('nav.download')}
            onClick={() => setDownloadOpen((open) => !open)}
          >
            <DownloadIcon />
            <span className={styles.label}>{t('nav.download')}</span>
          </button>
        </div>
      ) : null}
    </nav>
  );
}
